import { useState, useEffect } from 'react';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';
import { LayoutDashboard } from 'lucide-react';
import { MODULE_REGISTRY } from '@lingaai/types/graph/modules';
import {} from '@lingaai/shared-hooks';
import { useCurrentUser } from '@lingaai/user-management';
import { WidgetRenderer, type Widget } from '.';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface Dashboard {
  id: string;
  name: string;
  description: string | null;
}

interface DashboardProps {
  moduleId: string;
}

export function Dashboard({ moduleId }: DashboardProps) {
  const module = MODULE_REGISTRY[moduleId];
  const moduleName = module?.name || moduleId;
  const { data: user } = useCurrentUser();
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.tenant_id) return;

    const fetchDefaultDashboard = async () => {
      try {
        const response = await fetch(`${API_URL}/dashboards/default?tenant_id=${user.tenant_id}&module=${moduleId}`);
        if (response.ok) {
          const data = await response.json();
          setDashboard(data.dashboard);
          setWidgets(data.widgets || []);
        }
      } catch (error) {
        console.error('Failed to fetch default dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDefaultDashboard();
  }, [user?.tenant_id, moduleId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Calculate grid span based on widget width
  const getGridColumn = (width: number = 1): string => {
    return `span ${Math.min(width, 4)}`;
  };

  const getGridRow = (height: number = 1): string => {
    return `span ${Math.min(height, 3)}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', color: '#6366f1' }}>
          <LayoutDashboard size={ICON_SIZES.xl} strokeWidth={ICON_STROKE} />
        </Box>
        <Typography variant="h4">{dashboard?.name || `${moduleName} Dashboard`}</Typography>
      </Box>
      {dashboard?.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {dashboard.description}
        </Typography>
      )}

      {widgets.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Dashboard Configured
          </Typography>
          <Typography color="text.secondary">Create widgets from the Analytics section or AI assistant.</Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 3,
          }}
        >
          {widgets.map((widget) => (
            <Paper
              key={widget.id}
              sx={{
                p: 2,
                gridColumn: getGridColumn(widget.width),
                gridRow: getGridRow(widget.height),
                minHeight: widget.chart_type === 'kpi_card' ? 120 : 200,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                {widget.name}
              </Typography>
              <Box sx={{ height: widget.chart_type === 'kpi_card' ? 80 : 'calc(100% - 32px)' }}>
                <WidgetRenderer widget={widget} height={widget.chart_type === 'kpi_card' ? 80 : 280} module={moduleId} />
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}
